import React, { useEffect, useMemo, useState } from "react";
import { CalendarRange, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { authFetch } from "@/lib/auth";

interface PlannerItem {
  id: string;
  sow_date: string;
  transplant_date: string | null;
  expected_harvest_date: string | null;
  actual_harvest_date: string | null;
  status: "planned" | "sown" | "growing" | "harvesting" | "complete" | "failed";
  pasture_id: string;
  field_subdivision_id: string | null;
  crop_variety_id: string;
  succession_group_id: string | null;
  variety_name: string;
  crop_name: string;
  crop_category: string;
  pasture_name: string;
  field_name: string | null;
}

const STATUS_BAR_CLASS: Record<PlannerItem["status"], string> = {
  // Brand-aligned status palette. Background ~20-25% opacity for the wash;
  // foreground at full-strength brand color for AA contrast against the bar
  // wash AND against the sand page background.
  planned:    "bg-token-muted/15 text-charcoal border-token-muted/40 dark:bg-token-muted-dark/20 dark:text-charcoal-dark dark:border-token-muted-dark/40",
  sown:       "bg-burnt-orange/20 text-burnt-orange border-burnt-orange/50 dark:bg-burnt-orange-dark/25 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/50",
  growing:    "bg-olive/20 text-olive border-olive/50 dark:bg-olive-dark/25 dark:text-olive-dark dark:border-olive-dark/50",
  harvesting: "bg-burnt-orange/30 text-burnt-orange border-burnt-orange/60 dark:bg-burnt-orange-dark/35 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/60",
  complete:   "bg-moss/20 text-moss border-moss/50 dark:bg-moss-dark/25 dark:text-moss-dark dark:border-moss-dark/50",
  failed:     "bg-clay/15 text-clay border-clay/40 line-through dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/40",
};

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 32;
const LEFT_LABEL_WIDTH = 200;

function daysBetween(a: Date, b: Date): number {
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function startOfWeek(d: Date): Date {
  const out = new Date(d);
  const day = out.getUTCDay();
  out.setUTCDate(out.getUTCDate() - day);
  out.setUTCHours(0, 0, 0, 0);
  return out;
}

export function CropPlannerPage() {
  const [items, setItems] = useState<PlannerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupBy, setGroupBy] = useState<"crop" | "field">("crop");
  const [zoom, setZoom] = useState<"month" | "week" | "day">("month");

  useEffect(() => {
    (async () => {
      try {
        const data = await authFetch<{ items: PlannerItem[] }>("/api/plantings/planner");
        setItems(data.items ?? []);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load planner data";
        setError(msg);
        console.error("[CropPlanner]", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Calculate the timeline window: from earliest sow_date to latest harvest_date,
  // padded by 14 days on each side. Default to current month if no items.
  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    if (items.length === 0) {
      const now = startOfMonth(new Date());
      const end = new Date(now);
      end.setUTCMonth(end.getUTCMonth() + 3);
      return { rangeStart: now, rangeEnd: end, totalDays: daysBetween(now, end) };
    }
    let minDate = new Date(items[0].sow_date);
    let maxDate = new Date(items[0].sow_date);
    for (const p of items) {
      const sow = new Date(p.sow_date);
      if (sow < minDate) minDate = sow;
      const endStr = p.actual_harvest_date ?? p.expected_harvest_date ?? p.sow_date;
      const end = new Date(endStr);
      // Add 14 days for plantings without a harvest date so the bar has visible width
      if (!p.expected_harvest_date && !p.actual_harvest_date) {
        end.setUTCDate(end.getUTCDate() + 14);
      }
      if (end > maxDate) maxDate = end;
    }
    minDate.setUTCDate(minDate.getUTCDate() - 14);
    maxDate.setUTCDate(maxDate.getUTCDate() + 14);
    minDate = startOfMonth(minDate);
    maxDate.setUTCMonth(maxDate.getUTCMonth() + 1, 1); // Snap to next month start
    return {
      rangeStart: minDate,
      rangeEnd: maxDate,
      totalDays: daysBetween(minDate, maxDate),
    };
  }, [items]);

  // Pixel-per-day depending on zoom
  const pxPerDay = zoom === "month" ? 4 : zoom === "week" ? 14 : 40;
  const totalWidth = totalDays * pxPerDay;

  // Build the time-axis ticks (months when zoom=month, weeks when zoom=week, days when zoom=day)
  const ticks = useMemo(() => {
    const out: { label: string; offsetDays: number }[] = [];
    const cursor = new Date(rangeStart);
    if (zoom === "month") {
      while (cursor < rangeEnd) {
        const offset = daysBetween(rangeStart, cursor);
        const label = cursor.toLocaleDateString(undefined, {
          month: "short",
          year: "2-digit",
          timeZone: "UTC",
        });
        out.push({ label, offsetDays: offset });
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      }
    } else if (zoom === "week") {
      const wk = startOfWeek(cursor);
      while (wk < rangeEnd) {
        const offset = daysBetween(rangeStart, wk);
        const label = wk.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        });
        out.push({ label, offsetDays: offset });
        wk.setUTCDate(wk.getUTCDate() + 7);
      }
    } else {
      const d = new Date(cursor);
      while (d < rangeEnd) {
        const offset = daysBetween(rangeStart, d);
        if (offset % 7 === 0) {
          out.push({
            label: d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }),
            offsetDays: offset,
          });
        }
        d.setUTCDate(d.getUTCDate() + 1);
      }
    }
    return out;
  }, [rangeStart, rangeEnd, zoom]);

  // Group items into rows based on groupBy mode
  const rows = useMemo(() => {
    const map = new Map<string, { label: string; items: PlannerItem[] }>();
    for (const p of items) {
      let key: string;
      let label: string;
      if (groupBy === "crop") {
        key = p.crop_variety_id;
        label = `${p.crop_name} · ${p.variety_name}`;
      } else {
        key = p.field_subdivision_id ?? `${p.pasture_id}::whole`;
        label = p.field_name ? `${p.pasture_name} / ${p.field_name}` : p.pasture_name;
      }
      if (!map.has(key)) map.set(key, { label, items: [] });
      map.get(key)!.items.push(p);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [items, groupBy]);

  // Today marker offset (only show if it falls within the visible range)
  const todayOffset = useMemo(() => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    if (today < rangeStart || today > rangeEnd) return null;
    return daysBetween(rangeStart, today);
  }, [rangeStart, rangeEnd]);

  return (
    <>
      <PageHeader
        title="Crop Planner"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Crops", href: "/crops" },
          { label: "Planner" },
        ]}
      >
        <Button asChild className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
          <a href="/plantings">
            <Plus className="h-4 w-4 mr-2" /> New Planting
          </a>
        </Button>
      </PageHeader>

      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <Tabs value={groupBy} onValueChange={(v) => setGroupBy(v as "crop" | "field")}>
          <TabsList>
            <TabsTrigger value="crop">By Crop</TabsTrigger>
            <TabsTrigger value="field">By Field</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={zoom} onValueChange={(v) => setZoom(v as typeof zoom)}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="day">Day</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {items.length} planting{items.length === 1 ? "" : "s"}
        </span>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">Loading planner...</Card>
      ) : error ? (
        <Card className="p-6 text-center text-clay dark:text-clay-dark">{error}</Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarRange}
          title="No plantings yet"
          description="Create a planting to see it on the timeline."
          actionLabel="Go to Plantings"
          onAction={() => { window.location.href = "/plantings"; }}
        />
      ) : (
        <Card className="overflow-hidden">
          {/* Scrollable container — left label column is sticky inside scrollable parent */}
          <div className="overflow-x-auto">
            <div
              style={{
                width: totalWidth + LEFT_LABEL_WIDTH,
                minWidth: "100%",
                position: "relative",
              }}
            >
              {/* Header row: time axis */}
              <div
                className="flex border-b border-border bg-surface-raised dark:bg-surface-raised-dark sticky top-0 z-10"
                style={{ height: HEADER_HEIGHT }}
              >
                <div
                  className="border-r border-border flex items-center px-3 text-xs font-medium text-muted-foreground bg-surface-raised dark:bg-surface-raised-dark"
                  style={{ width: LEFT_LABEL_WIDTH, flexShrink: 0 }}
                >
                  {groupBy === "crop" ? "Crop / Variety" : "Pasture / Field"}
                </div>
                <div className="relative" style={{ width: totalWidth }}>
                  {ticks.map((t, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 flex items-center px-1 text-2xs text-muted-foreground border-l border-border/50"
                      style={{ left: t.offsetDays * pxPerDay, height: HEADER_HEIGHT }}
                    >
                      {t.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Today marker (vertical dashed line) */}
              {todayOffset !== null && (
                <div
                  className="absolute border-l-2 border-dashed border-burnt-orange dark:border-burnt-orange-dark pointer-events-none"
                  style={{
                    left: LEFT_LABEL_WIDTH + todayOffset * pxPerDay,
                    top: HEADER_HEIGHT,
                    bottom: 0,
                    zIndex: 5,
                  }}
                />
              )}

              {/* Rows */}
              {rows.map((row) => (
                <div
                  key={row.label}
                  className="flex border-b border-border last:border-b-0 hover:bg-surface-raised dark:bg-surface-raised-dark/50"
                  style={{ height: ROW_HEIGHT }}
                >
                  <div
                    className="border-r border-border flex items-center px-3 text-sm font-medium text-foreground bg-white"
                    style={{ width: LEFT_LABEL_WIDTH, flexShrink: 0 }}
                  >
                    <span className="truncate">{row.label}</span>
                  </div>
                  <div className="relative" style={{ width: totalWidth }}>
                    {/* Vertical grid lines aligned with ticks */}
                    {ticks.map((t, i) => (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0 border-l border-border/30"
                        style={{ left: t.offsetDays * pxPerDay }}
                      />
                    ))}

                    {/* Bars for each planting in this row */}
                    {row.items.map((p) => {
                      const sow = new Date(p.sow_date);
                      const endStr =
                        p.actual_harvest_date ?? p.expected_harvest_date ?? p.sow_date;
                      const end = new Date(endStr);
                      // Min 14-day visible bar for plantings without harvest dates
                      if (!p.expected_harvest_date && !p.actual_harvest_date) {
                        end.setUTCDate(end.getUTCDate() + 14);
                      }
                      const startOffset = daysBetween(rangeStart, sow);
                      const lengthDays = Math.max(1, daysBetween(sow, end));
                      return (
                        <div
                          key={p.id}
                          title={`${p.crop_name} · ${p.variety_name}\n${p.sow_date.split("T")[0]} → ${(p.expected_harvest_date ?? p.actual_harvest_date ?? "").split("T")[0]}\n${p.pasture_name}${p.field_name ? " / " + p.field_name : ""} · ${p.status}`}
                          className={`absolute rounded border text-xs px-2 flex items-center font-medium overflow-hidden whitespace-nowrap ${STATUS_BAR_CLASS[p.status]}`}
                          style={{
                            left: startOffset * pxPerDay,
                            width: lengthDays * pxPerDay,
                            top: 4,
                            bottom: 4,
                          }}
                        >
                          {groupBy === "crop"
                            ? p.field_name
                              ? `${p.pasture_name} / ${p.field_name}`
                              : p.pasture_name
                            : `${p.crop_name} · ${p.variety_name}`}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
        {(Object.keys(STATUS_BAR_CLASS) as PlannerItem["status"][]).map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded border ${STATUS_BAR_CLASS[s]}`} />
            <span className="capitalize">{s}</span>
          </div>
        ))}
      </div>
    </>
  );
}
